import { useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '../../styles/lib/utils';
import type { GridSpacing } from '../Grid';
import { Table, type TableColumn } from '../Table';
import { CategoryPill } from './CategoryPill';
import { defaultLayerInfo } from './layerInfo';
import { LayerNameInfoCell, LayerRemoveCell, LayerTableCellContext } from './layerTableCells';
import type { LayerInfo, LayerLibraryRow } from './types';

/** Fixed column widths — the table is exact-fit, never squeezed or horizontally scrolled. */
const COLUMN_WIDTHS = { name: 212, category: 200, remove: 48 } as const;

/**
 * The table's total rendered width: the columns plus the Table root's 1px border on
 * each side. Consumers hosting the table in a flex row size its container to exactly
 * this (with `shrink-0`) so the panel is never squeezed, cut off, or scrolling. When
 * the rows scroll vertically, the Category column (the table's flex column) yields the
 * scrollbar's width rather than forcing a horizontal scroll.
 */
export const SELECTED_LAYERS_TABLE_WIDTH = COLUMN_WIDTHS.name + COLUMN_WIDTHS.category + COLUMN_WIDTHS.remove + 2;

const COLUMNS: TableColumn<LayerLibraryRow>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Name',
    size: COLUMN_WIDTHS.name,
    cell: ({ row }) => <LayerNameInfoCell row={row.original} infoSide="left" />,
  },
  {
    id: 'category',
    accessorKey: 'category',
    header: 'Category',
    size: COLUMN_WIDTHS.category,
    cell: ({ row }) => <CategoryPill category={row.original.category} />,
  },
  {
    id: 'remove',
    header: '',
    size: COLUMN_WIDTHS.remove,
    cell: ({ row }) => <LayerRemoveCell row={row.original} />,
  },
];

export type SelectedLayersTableProps = {
  /** Stable id — used for the table root test id. */
  id?: string;
  /** The selected layers to list. */
  rows: LayerLibraryRow[];
  /** The newest added row, so we can highlight it. */
  justAddedRowId?: string;
  /** Called when a row's remove (trash) action is clicked. */
  onRemove?: (row: LayerLibraryRow) => void;
  /** Resolves the info-popover detail for a row (defaults to dummy data built from the row). */
  getLayerInfo?: (row: LayerLibraryRow) => LayerInfo;
  isLoading?: boolean;
  /** Rendered when there are no selected layers. */
  emptyState?: React.ReactNode;
  /** Row density. Defaults to `comfy`. */
  spacing?: GridSpacing;
  className?: string;
  /** Names the table for screen readers; prefer `aria-labelledby` when a visible heading sits above it. */
  'aria-label'?: string;
  /** Id of the visible heading that names the table for screen readers. */
  'aria-labelledby'?: string;
};

/**
 * Presentational "Selected" companion to {@link LayerLibraryTable}.
 *
 * Shows the same `LayerLibraryRow` data with fewer columns — Name (icon + info popover)
 * and Source — plus a remove (trash) action. Not sortable, searchable,
 * paginated, filterable, pinnable, or selectable. The consumer owns the selected set.
 */
function SelectedLayersTable({
  id = 'selected-layers-table',
  rows,
  justAddedRowId,
  onRemove,
  getLayerInfo = defaultLayerInfo,
  isLoading = false,
  emptyState,
  spacing = 'comfy',
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: SelectedLayersTableProps) {
  const [openInfoRowId, setOpenInfoRowId] = useState<string | null>(null);
  const [flashRowIds, setFlashRowIds] = useState<string[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const highlightedRowIds: Set<string> = useMemo(() => {
    return new Set([...flashRowIds, openInfoRowId].filter((id) => id != null));
  }, [openInfoRowId, flashRowIds]);
  const cellContext = useMemo(
    () => ({ openInfoRowId, setOpenInfoRowId, getLayerInfo, onRowAction: onRemove }),
    [openInfoRowId, getLayerInfo, onRemove],
  );

  useEffect(() => {
    if (typeof justAddedRowId === 'undefined') return;
    setFlashRowIds((prev) => [...prev, justAddedRowId]);
    setTimeout(() => setFlashRowIds((prev) => prev.filter((id) => id !== justAddedRowId)), 1000);
    scrollContainerRef.current
      ?.querySelector(`[data-row-id="${justAddedRowId}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
  }, [justAddedRowId]);

  // No pagination: one page sized to the data, so every selected layer always renders
  // (the page column scrolls). Without this the base Table's internal default
  // (pageSize 10) silently hides rows past 10 — there are no pager controls here.
  const showAllPagination = useMemo(() => ({ pageIndex: 0, pageSize: Math.max(rows.length, 1) }), [rows.length]);

  const showEmptyState = emptyState && !isLoading && rows.length === 0;

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col', className)} data-testid={id} ref={scrollContainerRef}>
      {showEmptyState ? (
        <div className="border-grey-20 bg-grey-5 flex flex-1 flex-col items-center justify-center gap-4 self-stretch rounded border p-8">
          {emptyState}
        </div>
      ) : (
        <LayerTableCellContext.Provider value={cellContext}>
          <Table
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            data={rows}
            columns={COLUMNS}
            getRowId={(row) => row.id}
            highlightedRowIds={highlightedRowIds}
            pagination={showAllPagination}
            spacing={spacing}
            enableSorting={false}
            enableRowSelection={false}
            enableColumnPinning={false}
            enableColumnFilters={false}
            enableColumnOrdering={false}
            enableHiding={false}
            enableColumnResizing={false}
            isLoading={isLoading}
            // Category flexes: it absorbs leftover width and yields to the vertical
            // scrollbar, so the columns always fit the exact-fit panel width.
            expandColumnId="category"
          />
        </LayerTableCellContext.Provider>
      )}
    </div>
  );
}

export { SelectedLayersTable };
